// Global Instructions Rule Applied!
import React, { useState, useEffect, useCallback, useMemo } from 'react'
import { Form, Alert, Row, Col, Tooltip } from 'antd'
import { toast } from 'react-toastify'
import { useParams } from 'react-router-dom'
import { useSelector, useDispatch } from 'react-redux'
import { selectRoundById } from '../../../state_manager/state_update_actions/rounds/selectors.ts'
import { addRound, removeRound, updateRoundRecord } from '../../../state_manager/state_update_actions/rounds/index.ts'
import { deleteRound, fetchRoundDeleteImpact, fetchRoundDetail } from '../services/roundsApi.ts'
import { updateDealRound } from '../../../state_manager/state_update_actions/deals/index.ts'
import { updateCompanyRound } from '../../../state_manager/state_update_actions/companies/index.ts'
import { RoundEntryFields } from '../../../state_manager/data_models/round_entry.ts'
import { AccountRecordFields } from '../../../state_manager/data_models/account_record.ts'
import { FormSection } from '../../../core/components/FormSection.tsx'
import { RecordFormDeleteEllipses } from '../../../core/components/RecordFormDeleteEllipses.tsx'
import { useTabManagerContext } from '../../../core/app-layout/lib/TabManagerContext.tsx'
import { showErrorToast } from '../../../core/utils/errorToast.tsx'
import { RoundDeleteImpactFields } from '../../../state_manager/data_models/round_delete_impact.ts'
import { InputManager } from '../../../core/components/InputManager/index.ts'
import { useLookups } from '../../global_configs/hooks/useLookups.ts'
import { useCurrencies } from '../../currencies/useCurrencies.ts'
import { getRoundDetailFields } from '../utils/fieldConfigs.ts'
import { SECTION_ROUND_INFORMATION } from '../../../state_manager/constants/strings.ts'
import { fetchCompanyById } from '../../companies/services/companiesService.ts'
import { updateCompanyRecord } from '../../../state_manager/state_update_actions/companies/index.ts'
import { getAccessToken } from '../../../core/auth/authService'
import { store } from '../../../state_manager/store.ts'

export default function FullRoundsForm({ recordId: propRecordId, isNew: propIsNew }) {
  const { urlRecordId } = useParams()
  const dispatch = useDispatch()

  const recordId = propRecordId !== undefined ? propRecordId : urlRecordId
  const isNew = propIsNew !== undefined ? propIsNew : recordId === 'new'

  // Get current round from state using selector
  const currentRound = useSelector((state) => (recordId && !isNew ? selectRoundById(state, recordId) : null))

  const [form] = Form.useForm()
  const [formData, setFormData] = useState({})
  const [coldLoadFailed, setColdLoadFailed] = useState(false)
  // What deleting this round would cost, read once alongside the round itself
  // so the Delete confirmation can say it rather than ask blind.
  const [deleteImpact, setDeleteImpact] = useState(null)
  const [deleting, setDeleting] = useState(false)
  const { closeFormTab } = useTabManagerContext()

  // Cold-load fetch: on a direct URL load nothing has seeded the rounds
  // slice, so fetch the round by id (view__round_detail) and seed it.
  useEffect(() => {
    if (isNew || !recordId || currentRound) return
    let cancelled = false
    ;(async () => {
      const token = getAccessToken(store.getState().auth)
      const result = await fetchRoundDetail(token, recordId)
      if (cancelled) return
      if (result.data) dispatch(addRound({ round: result.data }))
      else setColdLoadFailed(true)
    })()
    return () => {
      cancelled = true
    }
  }, [isNew, recordId, currentRound, dispatch])

  // Read the delete impact once the round id is known, so the Delete
  // confirmation states what goes with it instead of asking blind.
  useEffect(() => {
    if (isNew || !recordId) return
    let cancelled = false
    ;(async () => {
      const token = getAccessToken(store.getState().auth)
      const result = await fetchRoundDeleteImpact(token, recordId)
      if (!cancelled && result.data) setDeleteImpact(result.data)
    })()
    return () => {
      cancelled = true
    }
  }, [isNew, recordId])

  const { getLookupOptions } = useLookups()
  const { getCurrencyById, getCurrencyOptions } = useCurrencies()

  // Get companies from Redux for company lookup
  const companies = useSelector((state) => state.companies?.data || [])

  // Use currentRound directly (following deals/companies pattern)
  const data = currentRound || formData || {}

  // Local cache for companies fetched on demand
  const [fetchedCompanies, setFetchedCompanies] = useState({})

  // Fetch company when round has an account ID
  useEffect(() => {
    const accountId = data[RoundEntryFields.rounds__account]
    const currentToken = getAccessToken(store.getState().auth)
    if (
      accountId &&
      currentToken &&
      !fetchedCompanies[accountId] &&
      !companies.find((c) => {
        const cId = c[AccountRecordFields.accounts__id] || c.id
        return cId?.toString() === accountId.toString()
      })
    ) {
      fetchCompanyById(accountId, currentToken)
        .then((response) => {
          if (response.data && !response.error) {
            // Add to local cache and Redux
            setFetchedCompanies((prev) => ({ ...prev, [accountId]: response.data }))
            dispatch(updateCompanyRecord({ companyId: accountId.toString(), updates: response.data }))
          }
        })
        .catch((error) => {
          console.error('Error fetching company:', error)
        })
    }
  }, [data[RoundEntryFields.rounds__account], dispatch, fetchedCompanies, companies])

  // Get round ID for auto-patch
  const roundId =
    recordId || currentRound?.[RoundEntryFields.rounds__id] || data[RoundEntryFields.rounds__id] || data.id

  // Helper functions for InputManager field configurations
  const helpers = useMemo(
    () => ({
      getLookupOptions: (category) => {
        return getLookupOptions(category)
      },
      getCurrencyById: (currencyId) => {
        return getCurrencyById(currencyId)
      },
      getCurrencyOptions: () => {
        return getCurrencyOptions()
      },
      getCompanyDetailsById: (companyId) => {
        if (!companyId) {
          return null
        }

        // First, try to get company from fetchedCompanies cache
        let company = fetchedCompanies[companyId.toString()]

        // Then try Redux companies array
        if (!company) {
          company = companies.find((c) => {
            const cId = c[AccountRecordFields.accounts__id] || c.id
            return cId?.toString() === companyId.toString()
          })
        }

        if (!company) {
          return null
        }

        // Return company object with name/legal_name properties for display
        // getCompanyDisplayValue expects name or legal_name properties
        const companyName = company[AccountRecordFields.accounts__name] || company.name || ''
        const legalName = company[AccountRecordFields.accounts__legal_name] || company.legal_name || ''

        return {
          ...company,
          name: companyName || legalName,
          legal_name: legalName
        }
      }
    }),
    [getLookupOptions, getCurrencyById, getCurrencyOptions, companies, fetchedCompanies]
  )

  // Field configurations - use detail fields which includes all fields
  const roundFields = useMemo(() => getRoundDetailFields(data, helpers, false), [data, helpers])

  // Initialize form data from currentRound or empty for new
  useEffect(() => {
    if (isNew) {
      setFormData({})
    } else if (currentRound) {
      setFormData(currentRound)
    } else if (recordId && !currentRound) {
      // Round not found in Redux - show empty form or message
      setFormData({})
    }
  }, [currentRound, recordId, isNew])

  // Centralized handler for successful PATCH requests from InputManager auto-patch
  const handlePatchSuccess = useCallback(
    (patchRecordId, updates) => {
      if (!patchRecordId) return

      // Update round in Redux state
      dispatch(updateRoundRecord({ roundId: patchRecordId, updates }))

      // Update deals that reference this round
      dispatch(updateDealRound({ roundId: patchRecordId, updates }))

      // Update companies that have this round in their rounds array
      dispatch(updateCompanyRound({ roundId: patchRecordId, updates }))

      // Update local form data
      setFormData((prev) => ({ ...prev, ...updates }))
    },
    [dispatch]
  )

  // Handle field changes
  const handleFieldChange = useCallback((field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }))
  }, [])

  // Until 2026-09-21 a round could not be deleted anywhere in the app: the
  // only way to destroy one was unticking "Is Funding Round" on a linked deal,
  // which is exactly why that checkbox became a destructive control. Deleting
  // is now its own action, with the cascade and the guard in the RPC.
  const deleteBlocked = !!deleteImpact?.[RoundDeleteImpactFields.prog__blocked]

  const deleteDescription = useMemo(() => {
    if (!deleteImpact) return undefined
    if (deleteBlocked) {
      return 'This round is referenced by a cap table version or a legal agreement set. Re-point or trash those first.'
    }
    const instruments = deleteImpact[RoundDeleteImpactFields.prog__instruments] || 0
    const participations = deleteImpact[RoundDeleteImpactFields.prog__participations] || 0
    const deals = deleteImpact[RoundDeleteImpactFields.prog__linked_deals] || 0
    const goesWithIt = []
    if (instruments) goesWithIt.push(`${instruments} security link${instruments === 1 ? '' : 's'}`)
    if (participations) goesWithIt.push(`${participations} co-investor${participations === 1 ? '' : 's'}`)
    const sentences = ['The round will be moved to trash.']
    if (goesWithIt.length) sentences.push(`Its ${goesWithIt.join(' and ')} go with it.`)
    if (deals) {
      sentences.push(`${deals} linked deal${deals === 1 ? '' : 's'} will be unlinked, not deleted.`)
    }
    return sentences.join(' ')
  }, [deleteImpact, deleteBlocked])

  const handleDeleteRound = useCallback(async () => {
    if (!roundId) return
    setDeleting(true)
    try {
      const token = getAccessToken(store.getState().auth)
      const result = await deleteRound(token, roundId)
      if (!result.success) {
        showErrorToast(result.error || 'Failed to delete round')
        return
      }
      toast.success('Round deleted')
      dispatch(removeRound({ roundId }))
      // A bare removeTab strands the URL on the deleted record (see
      // useTabManager.closeFormTab) — and a hard refresh would resurrect it.
      closeFormTab(`rounds-form-edit-${roundId}`)
    } finally {
      setDeleting(false)
    }
  }, [roundId, dispatch, closeFormTab])

  // Cold-load fallback (hard-refresh rule): the slice is normally seeded by
  // whichever list opened the tab, but a direct URL load has no seeding —
  // fetch the round by id and seed the slice instead of declaring it missing.
  if (!isNew && recordId && !currentRound && !coldLoadFailed) {
    return (
      <div className='h-full flex items-center justify-center p-4 text-gray-400 text-sm'>
        Loading round…
      </div>
    )
  }

  // Show message if round not found (and not new)
  if (!isNew && recordId && !currentRound) {
    return (
      <div className='h-full flex items-center justify-center p-4'>
        <Alert
          message='Round Not Found'
          description="The round you're looking for is not available. It may have been deleted or you may not have access to it."
          type='warning'
          showIcon
        />
      </div>
    )
  }

  return (
    <div className='h-full flex flex-col overflow-auto'>
      <Form form={form} layout='vertical' className='h-full'>
        <div className='p-6'>
          <Row gutter={[16, 16]}>
            <Col xs={24} lg={14}>
              <FormSection
                title={SECTION_ROUND_INFORMATION}
                titleColor='blue'
                headerActions={
                  !isNew && roundId ? (
                    <Tooltip
                      title={
                        deleteBlocked
                          ? 'A cap table version or legal agreement set still references this round'
                          : ''
                      }
                    >
                      <span>
                        <RecordFormDeleteEllipses
                          confirmTitle='Are you sure you want to delete this round?'
                          confirmDescription={deleteDescription}
                          onDelete={handleDeleteRound}
                          disabled={deleteBlocked}
                          loading={deleting}
                        />
                      </span>
                    </Tooltip>
                  ) : null
                }
              >
                <InputManager
                  inputMode='read'
                  labelPosition='inline'
                  fields={roundFields}
                  onChange={(key, value) => handleFieldChange(key, value)}
                  enableAutoPatch={!!roundId}
                  patchId={roundId}
                  recordId={roundId}
                  onPatchSuccess={handlePatchSuccess}
                  className='round-fields'
                  fieldWrapping={true}
                  currentRecord={currentRound || data}
                />
              </FormSection>
            </Col>
          </Row>
        </div>
      </Form>
    </div>
  )
}
